import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import type { Team } from 'api-client';
import { useAppContext, useTeamsApi } from '@/context';
import TeamDetailsSettings from './TeamDetailsSettings';
import TeamMembersSettings from './TeamMembersSettings';
import TeamInvitationsSettings from './TeamInvitationsSettings';

export default function TeamSettings() {
  const apiClient = useTeamsApi();
  const { user } = useAppContext();
  const { teamSlug } = useParams<{ teamSlug: string }>();
  const [params, setParams] = useSearchParams();
  const tab = Number(params.get('tab') || 0);
  const activeTab = Number.isInteger(tab) && tab >= 0 && tab <= 2 ? tab : 0;
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [recentlySaved, setRecentlySaved] = useState<boolean>(false);
  const [retry, setRetry] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setTeam(null);
    setLoading(true);
    setError(null);
    setRecentlySaved(false);
    if (!teamSlug) {
      setLoading(false);
      setError('Team not found.');
      return;
    }
    apiClient
      .teamsList({ slug: teamSlug })
      .then((result) => {
        if (cancelled) return;
        const found = result.results?.find((value) => value.slug === teamSlug);
        if (found) setTeam(found);
        else setError('Team not found or access is unavailable.');
      })
      .catch(() => {
        if (!cancelled) setError('Could not load team settings. Please retry.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, teamSlug, retry]);

  const updateTeam = (updated: Team) => {
    if (updated.slug !== teamSlug || updated.name !== team?.name) {
      window.location.assign(`/teams/settings/${encodeURIComponent(updated.slug || '')}?${params.toString()}`);
      return;
    }
    setTeam(updated);
    setRecentlySaved(true);
  };
  const changeTab = (_: SyntheticEvent, value: number) => {
    const next = new URLSearchParams(params);
    next.set('tab', String(value));
    setParams(next);
  };
  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/teams/">
            Teams
          </Link>
          <Typography color="text.primary">{team ? `${team.name} Settings` : 'Settings'}</Typography>
        </Breadcrumbs>
        <Typography component="h1" variant="h4" mb={3}>
          Team Settings
        </Typography>
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress aria-label="Loading team settings" />
            <Typography>Loading team settings…</Typography>
          </Stack>
        ) : !team ? (
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {recentlySaved && <Alert severity="success">Changes saved successfully.</Alert>}
            <Paper sx={{ width: '100%', minWidth: 0 }}>
              <Tabs
                value={activeTab}
                onChange={changeTab}
                aria-label="Team settings"
                variant="scrollable"
                scrollButtons="auto"
              >
                {['Details', 'Members', 'Invitations'].map((label, index) => (
                  <Tab
                    key={label}
                    label={label}
                    id={`team-settings-tab-${index}`}
                    aria-controls={`team-settings-panel-${index}`}
                  />
                ))}
              </Tabs>
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  role="tabpanel"
                  hidden={activeTab !== index}
                  id={`team-settings-panel-${index}`}
                  aria-labelledby={`team-settings-tab-${index}`}
                >
                  {activeTab === index &&
                    (index === 0 ? (
                      <TeamDetailsSettings
                        team={team}
                        apiClient={apiClient}
                        onTeamUpdate={updateTeam}
                        onTeamDelete={() => window.location.assign('/teams/')}
                      />
                    ) : index === 1 ? (
                      <TeamMembersSettings team={team} currentUserId={user?.id} />
                    ) : (
                      <TeamInvitationsSettings team={team} apiClient={apiClient} onTeamUpdate={updateTeam} />
                    ))}
                </Box>
              ))}
            </Paper>
          </Stack>
        )}
      </Box>
    </Container>
  );
}
